<script setup lang="ts">
import { useClipboard } from '@vueuse/core';
import dayjs from 'dayjs';
import { ref } from 'vue';

import brokenPeanut from '@/assets/broken-peanut.png';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { ReviewStatus } from '@/lib/reviews';

defineProps<{
  createdDate?: string;
  image?: string;
  name: string;
  reviewStatus?: ReviewStatus;
}>();

defineEmits(['handle-close']);

const email = 'feedback@nuts.com';
const { copy, copied, isSupported } = useClipboard({ copiedDuring: 5000, source: email });

const isHovering = ref(false);
const toggleCopyHover = (hovering: boolean) => {
  isHovering.value = hovering;
};
</script>

<template>
  <div>
    <div
      class="px-4 py-6 border-b border-solid sm:flex sm:px-6 sm:py-8 sm:border-0 border-neutral-300"
    >
      <img
        v-if="image"
        alt=""
        class="flex object-contain mx-auto sm:block h-28 sm:h-48 shrink-0"
        :src="image"
      />
      <img
        v-else
        alt="placeholder image"
        class="flex object-contain mx-auto sm:block h-28 sm:h-48 shrink-0"
        :src="brokenPeanut"
      />
      <div class="w-full md:ml-4">
        <template v-if="reviewStatus === 'hasNotBoughtTheItemOrTooRecent'">
          <Header5 class="mt-6 text-black sm:mt-0"> Sorry, you cannot review {{ name }} </Header5>
          <BaseBodyText class="mt-2 text-black">
            You can only review products you’ve already purchased and received. Visit your Order
            History to review a different product you previously ordered and received.
          </BaseBodyText>
        </template>
        <template v-else>
          <Header5 class="mt-6 text-black sm:mt-0">
            You have already submitted a review for our “{{ name }}“
          </Header5>
          <BaseBodyText class="mt-2 text-black">
            Customers can only leave one review for each product they purchase.
            <template v-if="createdDate">
              Your review was submitted on {{ dayjs(createdDate).format('MM/DD/YYYY') }}
            </template>
          </BaseBodyText>
          <BaseBodyText class="inline mt-2 text-black">
            If you’d like to contact us about this decision, please email our customer service team
            at
          </BaseBodyText>
          <UnstyledButton
            @click="isSupported && copy(email)"
            @mouseover="toggleCopyHover(true)"
            @mouseleave="toggleCopyHover(false)"
          >
            <BaseBodyText class="mr-2 font-semibold text-nuts-cyan-800">
              {{ email }}
            </BaseBodyText>
          </UnstyledButton>
          <UnstyledButton
            v-if="isSupported"
            class="py-0.5 self-center px-2 text-white rounded bg-nuts-neutral-900 font-semibold inline-block"
            @click="isSupported && copy(email)"
            @mouseover="toggleCopyHover(true)"
            @mouseleave="toggleCopyHover(false)"
          >
            <Caption>{{ !copied ? 'Click to copy' : 'Copied to clipboard' }}</Caption>
          </UnstyledButton>
        </template>
        <ThemedButton
          class="w-full h-12 mt-6 sm:w-auto"
          size="default"
          theme="gray"
          @click="$emit('handle-close')"
          data-promo="1"
          data-promo-name="Review flow"
          :data-promo-creative="
            reviewStatus === 'hasNotBoughtTheItemOrTooRecent'
              ? 'Not eligible-not purchased'
              : 'Not eligible-already left review'
          "
        >
          Continue Shopping
        </ThemedButton>
      </div>
    </div>
  </div>
</template>
